import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowUpRight,
  Check,
  Droplets,
  Eye,
  FlaskConical,
  Plus,
  Sparkles,
} from 'lucide-react';
import { gauntlet, ApiError } from '../lib/api';
import { cx } from '../lib/format';
import { Badge, Button, Callout, CopyButton, Panel, Skeleton } from '../components/ui';

/**
 * Trying the wallet out with no money.
 *
 * A self-custody wallet looks broken until something arrives in it, and
 * "buy some Bitcoin first" is a terrible way to find out whether the software
 * works. Every other wallet leaves you to work this out; this page does not.
 */
export function TestingPage() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['testing'],
    queryFn: () => gauntlet.testing(),
    staleTime: Infinity,
  });

  const { data: walletData } = useQuery({ queryKey: ['wallets'], queryFn: () => gauntlet.wallets() });
  const wallet = walletData?.wallets.find((w) => w.type === 'hd') ?? walletData?.wallets[0];
  const added = new Set(wallet?.accounts.map((a) => a.chain_id) ?? []);

  const addChain = useMutation({
    mutationFn: (chainId: string) => gauntlet.addAccount(wallet!.id, chainId),
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries();
    },
    onError: (e) =>
      setError(
        e instanceof ApiError
          ? e.isLocked
            ? 'Unlock your wallet first — adding a chain derives new keys.'
            : e.message
          : 'Could not add that chain.',
      ),
  });

  if (isLoading || !data) return <Skeleton className="h-96 w-full" />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="inline-flex items-center gap-2 text-xl font-semibold tracking-tight">
          <FlaskConical size={19} className="text-brass" />
          Try it without money
        </h1>
        <p className="mt-1 text-sm text-ink-dim">
          Three ways to see the wallet actually work before you own any crypto. None of them cost
          anything or put a real coin at risk.
        </p>
      </div>

      {error && <Callout tone="danger">{error}</Callout>}

      {/* Guidance */}
      {data.guidance.map((section, index) => (
        <Panel key={section.title} title={`${index + 1}. ${section.title}`}>
          <div className="space-y-3">
            <p className="text-[13px] leading-relaxed text-ink-dim">{section.body}</p>
            {section.steps.length > 0 && (
              <ol className="space-y-1.5">
                {section.steps.map((step, i) => (
                  <li key={i} className="flex gap-2.5 text-[13px]">
                    <span className="num mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border border-hairline text-[10px] text-ink-faint">
                      {i + 1}
                    </span>
                    <span className="text-ink-dim">{step}</span>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </Panel>
      ))}

      {/* Testnets and faucets */}
      <section className="space-y-3">
        <div className="flex items-baseline justify-between gap-3">
          <h2 className="inline-flex items-center gap-1.5 text-sm font-semibold">
            <Droplets size={14} className="text-brass" />
            Test networks and faucets
          </h2>
          <span className="text-[11px] text-ink-faint">links checked {data.lastVerified}</span>
        </div>

        {data.chains.map((chain) => (
          <Panel key={chain.chainId}>
            <div className="space-y-3">
              <div className="flex flex-wrap items-start gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-sm font-medium">{chain.name}</span>
                    <Badge>{chain.symbol}</Badge>
                    {chain.recommended && <Badge tone="gain">start here</Badge>}
                  </div>
                  <p className="mt-1 text-[13px] leading-relaxed text-ink-dim">{chain.summary}</p>
                </div>

                {added.has(chain.chainId) ? (
                  <span className="inline-flex shrink-0 items-center gap-1 text-xs text-gain">
                    <Check size={13} /> added
                  </span>
                ) : (
                  <Button
                    size="sm"
                    icon={<Plus size={13} />}
                    disabled={!wallet}
                    loading={addChain.isPending && addChain.variables === chain.chainId}
                    onClick={() => addChain.mutate(chain.chainId)}
                  >
                    Add
                  </Button>
                )}
              </div>

              <div className="flex flex-wrap gap-1.5">
                {chain.faucets.map((faucet) => (
                  <a
                    key={faucet.url}
                    href={faucet.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="group inline-flex items-center gap-1.5 rounded-lg border border-hairline px-2.5 py-1.5 text-xs transition-colors hover:border-brass/40"
                    title={faucet.note}
                  >
                    <Droplets size={12} className="text-brass" />
                    <span>{faucet.name}</span>
                    {faucet.note && (
                      <span className="text-[10px] text-ink-faint">· {faucet.note}</span>
                    )}
                    <ArrowUpRight
                      size={11}
                      className="text-ink-faint transition-colors group-hover:text-brass"
                    />
                  </a>
                ))}
              </div>
            </div>
          </Panel>
        ))}

        <Callout tone="warn" title="Faucets come and go">
          They are run at their operators' own cost and close without notice. If one is dead, try
          the next — a broken link here is not a problem with your wallet.
        </Callout>
      </section>

      {/* Sample addresses */}
      <section className="space-y-3">
        <h2 className="inline-flex items-center gap-1.5 text-sm font-semibold">
          <Eye size={14} className="text-brass" />
          Public addresses to watch
        </h2>
        <p className="text-[13px] text-ink-dim">
          Paste one of these into a watch-only import to fill the app with genuine mainnet data
          immediately. These are famously public, so watching them reveals nothing about anyone.
        </p>

        {data.samples.map((sample) => (
          <Panel key={sample.address}>
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium">{sample.label}</span>
                <Badge>{sample.chainId}</Badge>
              </div>
              <p className="addr break-all rounded-lg border border-hairline bg-abyss p-2.5 text-[11px]">
                {sample.address}
              </p>
              <div className="flex items-center gap-2">
                <CopyButton value={sample.address} label="Copy address" />
              </div>
              <p className="text-[11px] leading-relaxed text-ink-faint">{sample.note}</p>
            </div>
          </Panel>
        ))}
      </section>

      <Callout tone="info" title="What a test coin cannot do">
        <span className="inline-flex items-start gap-1.5">
          <Sparkles size={13} className={cx('mt-0.5 shrink-0')} />
          <span>
            Testnet coins have no market price, so your portfolio will read zero however many you
            hold — that is correct, not a fault. Gift cards and on-ramps move real money and cannot
            be exercised with them.
          </span>
        </span>
      </Callout>
    </div>
  );
}
